import { useState, useEffect } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { formatDate, parseISO } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import { CampaignRequest } from '../../types/campaigns';
import {
    fetchCampaignRequests,
    fetchCampaignRequestContactAttemptsTotal,
    fetchCampaignMetadata,
    fetchStates,
} from '../../services/campaign';
import { useAuth } from '~/context/AuthProvider';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import StatusBadge from '~/components/CampaignRequestDetails/StatusBadge';
import { useQueries } from '@tanstack/react-query';
import { FaSearch, FaSync, FaTimes } from 'react-icons/fa';
import clip from 'text-clipper';
import { safeParseHtml } from '~/utils/safeParseHtml';

import { ReactSelectInput } from '~/common/form/Inputs';
import {
    datePresetSelectOptions,
    getDatePresets,
} from '~/utils/datePresets';
import React, { useMemo } from 'react';
import { Pagination } from '~/components/Pagination';
import { useCacheInvalidation } from '~/utils/cacheInvalidation';
import NotesModal from '~/components/Common/NotesModal';
import AddCampaignRequestDrawer from '~/components/CampaignRequestDetails/drawers/AddCampaignRequestDrawer';

const debounce = (
    func: (name: string, value: string) => void,
    delay: number
) => {
    let timeoutId: NodeJS.Timeout;
    return (name: string, value: string) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            func(name, value);
        }, delay);
    };
};
const columnHelper = createColumnHelper<CampaignRequest>();

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type FilterOption = {
    label: string;
    value: string;
};

type MedicalIssue = {
    id: number;
    name: string;
    medical_reason: {
        id: number;
        name: string;
    };
};

type Filters = {
    status: FilterOption[];
    medical_reason: FilterOption[];
    medical_issues: MedicalIssue[];
    region: FilterOption[];
    verifiedStatus: FilterOption[];
};

type SelectedFilters = {
    status: string;
    medical_reason: string;
    medical_issue: string;
    region: string;
    verifiedStatus: string;
    name: string;
    assignee: string;
    contact_attempts: string;
    assigned_to_me: string;
};

const initialFilters: SelectedFilters = {
    status: '',
    medical_reason: '',
    medical_issue: '',
    region: '',
    verifiedStatus: '',
    name: '',
    assignee: '',
    contact_attempts: '',
    assigned_to_me: '',
};

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const CampaignRequests: React.FC = () => {
    const { user } = useAuth();
    const isPSCOnly = user?.roles?.includes('patient services coordinator') && !user?.roles?.includes('administrator');
    const { refreshCampaignRequests } = useCacheInvalidation();
    const [searchParams] = useSearchParams();
    const [pageSize] = useState(100);
    const [pages, setPages] = useState<number>(1);

    const [page, setPage] = useState<number>(1);
    const [notesModal, setNotesModal] = useState<{
        created_by_name: string | undefined;
        created_date: string | undefined;
        isOpen: boolean;
        notes: string;
        title: string;
    }>({
        isOpen: false,
        notes: '',
        created_by_name: '',
        created_date: '',
        title: '',
    });
    const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

    const [sort, setSort] = useState<Sort | null>(null);
    const [filters, setFilters] = useState<Filters | null>(null);
    const pscDefaults = isPSCOnly ? { assigned_to_me: 'true' } : {};
    const [selectedFilters, setSelectedFilters] =
        useState<SelectedFilters>(() => {
            const urlAssignee = searchParams.get('assignee');
            const urlStatus = searchParams.get('status');
            const hasUrlParams = urlAssignee || urlStatus;
            try {
                const saved = localStorage.getItem('campaignRequestFilters');
                const base = saved ? { ...initialFilters, ...pscDefaults, ...JSON.parse(saved) } : { ...initialFilters, ...pscDefaults };
                if (hasUrlParams) {
                    return {
                        ...base,
                        ...(urlAssignee && { assignee: urlAssignee }),
                        ...(urlStatus && { status: urlStatus }),
                    };
                }
                return base;
            } catch {
                if (hasUrlParams) {
                    return {
                        ...initialFilters,
                        ...pscDefaults,
                        ...(urlAssignee && { assignee: urlAssignee }),
                        ...(urlStatus && { status: urlStatus }),
                    };
                }
                return { ...initialFilters, ...pscDefaults };
            }
        });
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        try {
            const saved = localStorage.getItem('campaignRequestDateRange');
            return saved ? JSON.parse(saved) : initialDateRange;
        } catch {
            return initialDateRange;
        }
    });
    const [showDateFilters, setShowDateFilters] = useState(false);
    const [selectedDatePreset, setSelectedDatePreset] = useState('');
    const [textInputResetKey, setTextInputResetKey] = useState(0);
    const datePresets = getDatePresets();

    const isDateFilterActive = !!(dateRange.start_date && dateRange.end_date);

    const results = useQueries({
        queries: [
            {
                queryKey: [
                    'campaign-requests',
                    { pageSize, sort, sortBy, page },
                    selectedFilters,
                    dateRange,
                ],
                queryFn: () =>
                    fetchCampaignRequests(
                        { pageSize, sort, sortBy },
                        selectedFilters,
                        dateRange,
                        page
                    ),
                staleTime: 0,
            },
            {
                queryKey: ['campaign_metadata'],
                queryFn: () => fetchCampaignMetadata(),
                staleTime: Infinity,
            },
            {
                queryKey: ['states'],
                queryFn: () => fetchStates(),
                staleTime: Infinity,
            },
            {
                queryKey: [
                    'campaign-requests-contact-attempts-total',
                    selectedFilters,
                    dateRange,
                ],
                queryFn: () =>
                    fetchCampaignRequestContactAttemptsTotal(
                        selectedFilters,
                        dateRange
                    ),
                enabled: isDateFilterActive,
                staleTime: 0,
            },
        ],
    });

    const campaignResults = results[0]?.data;
    const metadata = results[1]?.data;
    const states = results[2]?.data;
    const contactAttemptsTotal = results[3]?.data?.total;
    const isContactAttemptsTotalLoading = results[3]?.isLoading;

    useEffect(() => {
        const total = campaignResults?.count;
        if (total) {
            setPages(Math.ceil(total / pageSize));
        }
    }, [campaignResults, pageSize]);

    useEffect(() => {
        try {
            localStorage.setItem('campaignRequestFilters', JSON.stringify(selectedFilters));
        } catch {}
    }, [selectedFilters]);

    useEffect(() => {
        try {
            localStorage.setItem('campaignRequestDateRange', JSON.stringify(dateRange));
        } catch {}
        if (!dateRange.start_date && !dateRange.end_date && selectedDatePreset) {
            setSelectedDatePreset('');
        }
    }, [dateRange, selectedDatePreset]);

    useEffect(() => {
        // Hardcoded status options
        const statusOptions = [
            { label: 'New', value: 'New' },
            { label: 'Contacted', value: 'Contacted' },
            { label: 'Approved', value: 'Approved' },
            { label: 'Rejected', value: 'Rejected' },
            { label: 'Archived', value: 'Archived' },
            { label: 'Unassigned', value: 'Unassigned' },
        ];
        const verifiedOptions = [
            { label: 'Verified', value: 'verified' },
            { label: 'Unverified', value: 'unverified' },
        ];
        if (!filters && metadata) {
            const newFilters = {
                status: statusOptions,
                medical_reason:
                    metadata?.medical_reasons?.map(
                        (reason: { name: string; id: number }) => ({
                            label: reason.name,
                            value: String(reason.id),
                        })
                    ) || [],
                medical_issues: metadata?.medical_issues || [],
                region:
                    metadata?.regions?.map(
                        (region: { name: string; code: string }) => ({
                            label: region.name,
                            value: region.code,
                        })
                    ) || [],
                verifiedStatus: verifiedOptions,
            };
            setFilters(newFilters);
        }
    }, [filters, metadata]);

    const isLoading = results.some((result) => result.isLoading);
    const isFetching = results.some((result) => result.isFetching);
    const error = results.find((result) => result.error);

    // if (isLoading) return <p>Loading...</p>;

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            if (sort === Sort.ASC) {
                setSort(Sort.DESC);
            } else {
                setSort(Sort.ASC);
            }
        } else {
            setSortBy(columnId);
            const descFirstColumns = ['contact_attempts_count', 'created_date', 'approve_date', 'latest_note'];
            setSort(descFirstColumns.includes(columnId) ? Sort.DESC : Sort.ASC);
        }
    };

    const handleFilter = (filterType: string, value: string) => {
        const newFilters = {
            ...selectedFilters,
            [filterType]: value,
        };

        if (filterType === 'medical_reason') {
            newFilters.medical_issue = '';
        }

        setSelectedFilters(newFilters);
        if (page !== 1) setPage(1);
    };
    const handleDateRange = (dateRangeType: string, value: string) => {
        setDateRange({ ...dateRange, [dateRangeType]: value });
        setSelectedDatePreset('');
        if (page !== 1) setPage(1);
    };

    const handleDatePresetChange = (presetValue: string) => {
        setSelectedDatePreset(presetValue);
        const preset = datePresets.find((p) => p.value === presetValue);
        if (preset) {
            const range = preset.getRange();
            setDateRange({ start_date: range.start, end_date: range.end });
            if (page !== 1) setPage(1);
        }
    };

    const debouncedHandleFilter = debounce((name: string, value: string) => {
        setPage(1);
        setSelectedFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, 500);

    const handleClearFilters = () => {
        setPage(1);
        setSelectedFilters(initialFilters);
        setDateRange(initialDateRange);
        localStorage.removeItem('campaignRequestFilters');
        localStorage.removeItem('campaignRequestDateRange');
        setTextInputResetKey((k) => k + 1);
    };

    const hasActiveFilters = Object.entries(selectedFilters).some(
        ([key, value]) => value !== initialFilters[key as keyof SelectedFilters]
    ) || dateRange.start_date || dateRange.end_date;

    const columns = useMemo(() => {
        const baseColumns = [
            columnHelper.accessor(
                (props) =>
                    props.created_date
                        ? formatDate(parseISO(props.created_date), 'MM/dd/yy')
                        : '',
                {
                    id: 'created_date',
                    header: () => 'Date Created',
                    cell: (props) => {
                        const createdDate = props.row.original.created_date;
                        if (!createdDate) return '';
                        
                        const date = parseISO(createdDate);
                        const dateStr = formatDate(date, 'MM/dd/yy');
                        const timeStr = formatDate(date, 'h:mm a');
                        
                        return (
                            <div>
                                <div>{dateStr}</div>
                                <div className='text-sm text-secondary'>{timeStr}</div>
                            </div>
                        );
                    },
                }
            ),
            columnHelper.accessor(
                (props) =>
                    `${props.initial_first_name} ${props.initial_last_name}`,
                {
                    id: 'initial_first_name',
                    header: () => 'Contact',
                    cell: (props) => {
                        return (
                            <Link
                                to={`/campaign-requests/${props.row.original.id}`}
                            >
                                <div className='font-bold text-primary'>
                                    {props.renderValue()}
                                </div>
                                <div className='text-sm text-secondary'>
                                    {props.row.original.campaign_is_for
                                        ? `${props.row.original.campaign_is_for.name}`
                                        : ''}
                                </div>
                            </Link>
                        );
                    },
                }
            ),
            columnHelper.accessor(
                (props) => {
                    const firstName = props.patient_first_name || '';
                    const lastName = props.patient_last_name || '';
                    return `${firstName} ${lastName}`.trim();
                },
                {
                    id: 'patient_first_name',
                    header: () => 'Client',
                    cell: (props) => props.renderValue() || '',
                }
            ),
            columnHelper.accessor('status.name', {
                header: () => 'Status',
                id: 'status__name',
                cell: (props) => (
                    <StatusBadge status={props.getValue() || ''} />
                ),
            }),
            columnHelper.accessor(
                (props) => props.contact_attempts_count || 0,
                {
                    header: () => 'Contact Attempts',
                    cell: (props) => {
                        const count = props.renderValue();
                        return count || 0;
                    },
                    id: 'contact_attempts_count',
                }
            ),
            columnHelper.accessor('patient_services_coordinator', {
                header: () => 'CSC',
                id: 'patient_services_coordinator__person__first_name',
                cell: (props) => {
                    const coordinator = props.getValue();
                    const person = coordinator?.person;
                    const firstName = person?.first_name || '';
                    const lastName = person?.last_name || '';
                    return `${firstName} ${lastName}`;
                },
            }),
            columnHelper.accessor('medical_reason', {
                header: () => 'Medical Reason',
                cell: (props) => {
                    const medicalReason = props.getValue();
                    const medicalIssue = props.row.original.primary_medical_issue;
                    return (
                        <div>
                            <div>{medicalReason?.name || ''}</div>
                            {medicalIssue?.name && (
                                <div className='text-sm text-secondary'>
                                    {medicalIssue.name}
                                </div>
                            )}
                        </div>
                    );
                },
                id: 'medical_reason__name',
            }),
        ];

        // Conditionally add Medical Issue column if filtering by medical issue
        if (selectedFilters.medical_issue) {
            baseColumns.push(
                columnHelper.accessor(
                    (props) => props.primary_medical_issue?.name || '',
                    {
                        header: () => 'Medical Issue',
                        cell: (props) => {
                            return props.renderValue();
                        },
                        id: 'primary_medical_issue__name',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    }
                ) as any
            );
        }

        // Add remaining columns
        baseColumns.push(
            columnHelper.accessor((props) => props.latest_note?.note || props.notes || '', {
                id: 'latest_note',
                header: () => 'Latest Note',
                size: 300,
                minSize: 200,
                cell: (props) => {
                    const latestNote = props.row.original.latest_note;
                    const noteText = latestNote?.note || props.row.original.notes || '';
                    const hasNote = noteText.length > 0;
                    // A contact attempt's date + method is meaningful on its own,
                    // even with no typed note — coordinators track the call/email
                    // cadence by it (HHLTB-2363). Show the date/method line in that
                    // case too.
                    const isContactAttempt =
                        latestNote?.type === 'contact_attempt';
                    const showDateLine =
                        !!latestNote?.created_date &&
                        (hasNote || isContactAttempt);

                    const handleNotesClick = () => {
                        if (hasNote) {
                            setNotesModal({
                                isOpen: true,
                                notes: noteText,
                                created_by_name: latestNote?.created_by_name,
                                created_date:
                                    latestNote?.created_date || undefined,
                                title: `${latestNote ? 'Latest Note' : 'Notes'} - ${
                                    props.row.original.initial_first_name || ''
                                } ${
                                    props.row.original.initial_last_name || ''
                                }`,
                            });
                        }
                    };

                    return (
                        <div
                            className={`${
                                hasNote
                                    ? 'cursor-pointer transition-colors hover:text-primary'
                                    : 'text-gray-400'
                            }`}
                            onClick={handleNotesClick}
                            title={hasNote ? 'Click to view full note' : ''}
                        >
                            <div
                                className='line-clamp-2 max-w-xs [&_a]:text-primary [&_a]:underline'
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {hasNote
                                    ? safeParseHtml(
                                          clip(noteText, 140, {
                                              html: true,
                                              maxLines: 2,
                                          })
                                      )
                                    : ''}
                            </div>
                            {showDateLine && latestNote?.created_date && (
                                <div className='mt-1 text-sm text-gray-400'>
                                    {formatDate(
                                        parseISO(latestNote.created_date),
                                        'MM/dd/yy'
                                    )}
                                    {latestNote.type === 'contact_attempt' &&
                                        latestNote.method_display &&
                                        ` · ${latestNote.method_display}`}
                                </div>
                            )}
                        </div>
                    );
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any
        );

        return baseColumns;
    }, [selectedFilters.medical_issue]);

    const table = useReactTable({
        data: campaignResults?.results || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    if (error) return <p>Error: {String(error) || 'An error occurred'}</p>;

    return (
        <>
            <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-4'>
                    <h1 className='text-title-md2 font-semibold text-black dark:text-white'>
                        Campaign Requests
                    </h1>
                    <button
                        type='button'
                        onClick={() => setIsAddDrawerOpen(true)}
                        className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                    >
                        <FaPlus />
                        Add Request
                    </button>
                </div>

                <nav>
                    <ol className='flex items-center gap-2'>
                        <li>
                            <Link to='/'>My Dashboard /</Link>
                        </li>
                        <li className='font-bold text-primary'>Campaign Requests</li>
                    </ol>
                </nav>
            </div>

            {/* table */}

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-4 overflow-x-auto px-8 pb-4'>
                    <div className='relative min-w-[175px] max-w-[175px]'>
                        <input
                            key={`contact-${textInputResetKey}`}
                            name='contact'
                            placeholder='Contact or Client'
                            className='h-[36px] w-full rounded border border-stroke px-3 pr-10 text-sm outline-none focus:border-primary'
                            defaultValue={selectedFilters.name}
                            onChange={(event) =>
                                debouncedHandleFilter(
                                    'name',
                                    event.target.value
                                )
                            }
                        />
                        <FaSearch
                            color='gray'
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                        />
                    </div>
                    <div className='relative min-w-[175px] max-w-[175px]'>
                        <input
                            key={`csc-${textInputResetKey}`}
                            name='csc'
                            placeholder='Assigned CSC'
                            className='h-[36px] w-full rounded border border-stroke px-3 pr-10 text-sm outline-none focus:border-primary'
                            defaultValue={selectedFilters.assignee}
                            onChange={(event) =>
                                debouncedHandleFilter(
                                    'assignee',
                                    event.target.value
                                )
                            }
                        />
                        <FaSearch
                            color='gray'
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                        />
                    </div>
                    {isPSCOnly && (
                        <label className='inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-gray-600'>
                            <input
                                type='checkbox'
                                checked={selectedFilters.assigned_to_me === 'true'}
                                onChange={(e) =>
                                    handleFilter('assigned_to_me', e.target.checked ? 'true' : '')
                                }
                                className='h-4 w-4 rounded border-gray-300 accent-primary'
                            />
                            My Requests
                        </label>
                    )}
                    <div className='relative'>
                        {!filters || !filters?.status ? (
                            <div className='w-[120px] animate-pulse'>
                                <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                            </div>
                        ) : (
                            <div className='min-w-[150px]'>
                                <ReactSelectInput
                                    options={filters?.status || []}
                                    value={selectedFilters.status}
                                    onChange={(value) =>
                                        handleFilter('status', value)
                                    }
                                    placeholder='Status'
                                />
                            </div>
                        )}
                    </div>
                    <div className='relative'>
                        <div className='min-w-[175px]'>
                            <ReactSelectInput
                                options={[
                                    { label: '0', value: '0' },
                                    { label: '1', value: '1' },
                                    { label: '2', value: '2' },
                                    { label: '3', value: '3' },
                                    { label: '4+', value: '4+' },
                                ]}
                                value={selectedFilters.contact_attempts}
                                onChange={(value) =>
                                    handleFilter('contact_attempts', value)
                                }
                                placeholder='Contact Attempts'
                            />
                        </div>
                    </div>
                    <div className='relative'>
                        {!filters || !filters?.medical_reason ? (
                            <div className='w-[180px] animate-pulse'>
                                <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                            </div>
                        ) : (
                            <div className='min-w-[180px]'>
                                <ReactSelectInput
                                    options={filters?.medical_reason || []}
                                    value={selectedFilters.medical_reason}
                                    onChange={(value) =>
                                        handleFilter('medical_reason', value)
                                    }
                                    placeholder='Medical Reason'
                                />
                            </div>
                        )}
                    </div>
                    {selectedFilters.medical_reason &&
                        filters?.medical_issues && (
                            <div className='relative'>
                                <div className='min-w-[175px] max-w-[175px]'>
                                    <ReactSelectInput
                                        options={filters.medical_issues
                                            .filter(
                                                (issue) =>
                                                    issue.medical_reason &&
                                                    String(issue.medical_reason.id) ===
                                                    String(selectedFilters.medical_reason)
                                            )
                                            .map((issue) => ({
                                                label: issue.name,
                                                value: String(issue.id),
                                            }))}
                                        value={selectedFilters.medical_issue}
                                        onChange={(value) =>
                                            handleFilter('medical_issue', value)
                                        }
                                        placeholder='Medical Issue'
                                    />
                                </div>
                            </div>
                        )}
                    <button
                        type='button'
                        onClick={() => setShowDateFilters(!showDateFilters)}
                        className={`flex h-[36px] items-center gap-1.5 rounded border px-3 text-sm transition-colors ${
                            showDateFilters || dateRange.start_date || dateRange.end_date
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-[#E2E8F0] text-gray-600 hover:border-primary hover:text-primary'
                        }`}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={1.5}
                            stroke='currentColor'
                            className='h-4 w-4'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'
                            />
                        </svg>
                        Created Date
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={2}
                            stroke='currentColor'
                            className={`h-3 w-3 transition-transform ${showDateFilters ? 'rotate-180' : ''}`}
                        >
                            <path strokeLinecap='round' strokeLinejoin='round' d='m19.5 8.25-7.5 7.5-7.5-7.5' />
                        </svg>
                    </button>
                    <button
                        type='button'
                        onClick={() => refreshCampaignRequests()}
                        className={`text-gray-600 hover:bg-gray-50 inline-flex shrink-0 items-center justify-center rounded p-2 transition-colors duration-300 ${
                            isFetching ? 'cursor-wait opacity-60' : ''
                        }`}
                        title='Refresh data'
                        disabled={isFetching}
                        aria-busy={isFetching}
                    >
                        <FaSync
                            className={`text-base ${
                                isFetching ? 'animate-spin' : ''
                            }`}
                        />
                    </button>
                    {hasActiveFilters && (
                        <button
                            type='button'
                            onClick={handleClearFilters}
                            className='inline-flex shrink-0 items-center gap-1.5 rounded border border-[#E2E8F0] px-3 py-1.5 text-xs text-gray-500 transition-colors hover:border-danger hover:text-danger'
                            title='Clear all filters'
                        >
                            <FaTimes className='text-[10px]' />
                            Clear Filters
                        </button>
                    )}
                </div>

                {showDateFilters && (
                    <div className='flex flex-wrap items-center gap-4 px-8 pb-4'>
                        <span className='text-sm text-gray-600'>
                            Filter by created date:
                        </span>
                        <div className='min-w-[180px]'>
                            <ReactSelectInput
                                options={datePresetSelectOptions}
                                value={selectedDatePreset}
                                onChange={handleDatePresetChange}
                                placeholder='Select date range'
                            />
                        </div>
                        <input
                            type='date'
                            name='start_date'
                            className='rounded border border-[#E2E8F0] p-[6px]'
                            value={dateRange.start_date}
                            onChange={(event) =>
                                handleDateRange('start_date', event.target.value)
                            }
                        />
                        <span className='text-sm text-gray-500'>to</span>
                        <input
                            type='date'
                            name='end_date'
                            className='rounded border border-[#E2E8F0] p-[6px]'
                            value={dateRange.end_date}
                            onChange={(event) =>
                                handleDateRange('end_date', event.target.value)
                            }
                        />
                        {(dateRange.start_date || dateRange.end_date) && (
                            <button
                                type='button'
                                onClick={() => {
                                    setDateRange(initialDateRange);
                                    setPage(1);
                                }}
                                className='text-sm text-gray-500 hover:text-danger'
                            >
                                Clear dates
                            </button>
                        )}
                    </div>
                )}

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(pageSize, (campaignResults?.count || 0) - (page - 1) * pageSize)} of {campaignResults?.count || 0} requests</p>

                    {isDateFilterActive && (
                        <p className='font-semibold'>
                            Total Contact Attempts:{' '}
                            {isContactAttemptsTotalLoading
                                ? '…'
                                : (contactAttemptsTotal ?? 0).toLocaleString()}
                        </p>
                    )}

                    {/* <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdClose className='text-red' size={18} />
                            Deactivate
                        </div>
                    </div> */}
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const useChevron =
                                            header.column.id === sortBy;
                                        let chevron = null;
                                        if (useChevron) {
                                            if (sort === Sort.ASC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            } else if (sort === Sort.DESC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            }
                                        }

                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            {chevron}
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              className='text-right'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          >
                                              <CampaignActionDropdown
                                                  campaignId={
                                                      row?.original?.campaign
                                                          ?.campaign_id
                                                  }
                                              />
                                          </td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={campaignResults?.count} pageSize={pageSize} />
            </section>

            <NotesModal
                isOpen={notesModal.isOpen}
                notes={notesModal.notes}
                created_by_name={notesModal.created_by_name}
                created_date={notesModal.created_date}
                title={notesModal.title}
                onClose={() =>
                    setNotesModal({
                        isOpen: false,
                        notes: '',
                        created_by_name: '',
                        created_date: '',
                        title: '',
                    })
                }
            />

            <AddCampaignRequestDrawer
                isOpen={isAddDrawerOpen}
                setIsOpen={setIsAddDrawerOpen}
                metadata={metadata}
                states={states || []}
                onSuccess={() => {
                    refreshCampaignRequests();
                }}
            />
        </>
    );
};

export default CampaignRequests;
